import React from 'react';
import PropTypes from 'prop-types';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

class StripeCardElement extends React.Component {
    static propTypes = {
        payment_processor: PropTypes.shape({
            shouldShowBillingDetails: PropTypes.func.isRequired,
            __stripe_form: PropTypes.any,
            _properties: PropTypes.shape({
                publishable_key: PropTypes.string,
            }),
        }).isRequired,
        t: PropTypes.func.isRequired,
    };

    constructor() {
        super();

        this.stripe = null;
        this.elements = null;
        this.card = null;
        this.credit_card_number_valid = false;
        this.credit_card_expiry_valid = false;
        this.state = {
            cardholder_name: '',
            first_name: '',
            last_name: '',
            address_1: '',
            address_2: '',
            city: '',
            region: '',
            post_code: '',
            loading: true,
        };
    }

    componentDidMount() {
        Mobi2Go.UI.bind(
            'invalidCreditCardDetailsWarning',
            this.invalidDetailsWarning
        );
        this.setState(
            {
                cardholder_name: '',
                first_name: '',
                last_name: '',
                address_1: '',
                address_2: '',
                city: '',
                region: '',
                post_code: '',
                loading: true,
            },
            () => {
                if (window.Stripe && window.Stripe.StripeV3) {
                    this.setUpForm();
                    return;
                }
                const src = [
                    'https://js.stripe.com/v2/',
                    'https://js.stripe.com/v3/',
                ];
                const is_external = true;
                Mobi2Go.helpers.loadScripts(src, is_external, this.setUpForm);
            }
        );

        /*  fix known Stripe issue with Safari 14
         *   forcing credit card name field to re-render
         *   readmore here: https://github.com/stripe/react-stripe-js/issues/136
         */
        setTimeout(() => {
            const contentForRerender = document.querySelector(
                '.__PrivateStripeElement'
            );
            if (contentForRerender) {
                const initialDisplay = contentForRerender.style.display;
                contentForRerender.style.display = 'none';
                contentForRerender.offsetHeight;
                contentForRerender.style.display = initialDisplay;
            }
        }, 200);
    }

    componentWillUnmount() {
        Mobi2Go.UI.unbind(
            'invalidCreditCardDetailsWarning',
            this.invalidDetailsWarning
        );
        if (this.card) {
            this.card.unmount();
        }
    }

    setUpForm = () => {
        if (!this.card_element) {
            return;
        }
        const { payment_processor } = this.props;
        this.stripe = window.Stripe.StripeV3(
            payment_processor._properties.publishable_key
        );
        this.elements = this.stripe.elements({
            locale: Mobi2Go.app.language,
        });

        const card_element_styles = getComputedStyle(this.card_element);

        this.card = this.elements.create('card', {
            hidePostalCode: true,
            iconStyle: 'solid',
            style: {
                base: {
                    iconColor: card_element_styles.textDecorationColor,
                    color: card_element_styles.color,
                    fontWeight: card_element_styles.fontWeight,
                    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                    fontSize: card_element_styles.fontSize,
                    fontSmoothing: 'antialiased',
                    '::placeholder': {
                        color: card_element_styles.textDecorationColor,
                    },
                    ":-webkit-autofill": { color: card_element_styles.color}
                },
                invalid: {
                    iconColor: '#e85746',
                    color: '#e85746',
                },
            },
            classes: {
                focus: 'is-focused',
            },
        });

        this.card.on('ready', () => this.setState({ loading: false }));
        this.card.on('change', this.onCardChange);
        this.card.mount(this.card_element);
    };

    onCardChange = result => {
        this.credit_card_number_valid = false;
        this.credit_card_expiry_valid = false;
        if (result.complete === true) {
            this.credit_card_number_valid = true;
            this.credit_card_expiry_valid = true;
            return;
        }
        if (
            result.error !== undefined &&
            result.error.code === 'invalid_expiry_year_past'
        ) {
            // If stripe says expiry is wrong
            // it means number is ok since it would report card number error first
            this.credit_card_number_valid = true;
        }
    };

    isCreditCardNumberValid = () => {
        return this.credit_card_number_valid;
    };

    isCreditCardExpiryValid = () => {
        return this.credit_card_expiry_valid;
    };

    isBillingDetailsValid = () => {
        const { payment_processor } = this.props;
        if (!payment_processor.shouldShowBillingDetails()) {
            return true;
        }
        const {
            first_name,
            last_name,
            address_1,
            city,
            region,
            post_code,
        } = this.state;
        return (
            !!first_name &&
            !!last_name &&
            !!address_1 &&
            !!city &&
            !!region &&
            !!post_code
        );
    };

    isCardholderNameValid = () => {
        const { payment_processor } = this.props;
        if (payment_processor.shouldShowBillingDetails()) {
            return true;
        }
        const { cardholder_name } = this.state;
        return !!cardholder_name;
    };

    isExtraDetailsValid = () => {
        return this.isBillingDetailsValid() && this.isCardholderNameValid();
    };

    handleAction = (client_secret, callback) => {
        this.stripe
            .handleCardAction(client_secret)
            .then(({ error, paymentIntent: payment_intent }) => {
                if (error) {
                    callback(error);
                    return;
                }
                callback(null, payment_intent);
            })
            .catch(error => callback(error));
    };

    createPaymentMethod = callback => {
        const extra_details = { billing_details: { address: {} } };
        const {
            first_name,
            last_name,
            address_1,
            address_2,
            city,
            region,
            post_code,
            cardholder_name,
        } = this.state;
        const { payment_processor } = this.props;
        if (payment_processor.shouldShowBillingDetails()) {
            if (first_name && last_name) {
                extra_details.billing_details.name = `${first_name} ${last_name}`;
            }
            if (address_1) {
                extra_details.billing_details.address.line1 = address_1;
            }
            if (address_2) {
                extra_details.billing_details.address.line2 = address_2;
            }
            if (city) {
                extra_details.billing_details.address.city = city;
            }
            if (region) {
                extra_details.billing_details.address.state = region;
            }
            if (post_code) {
                extra_details.billing_details.address.postal_code = post_code;
            }
        } else {
            extra_details.billing_details.name = cardholder_name;
        }
        this.stripe
            .createPaymentMethod('card', this.card, extra_details)
            .then(result => {
                callback(null, {
                    is_payment_method: true,
                    payment_method: result.paymentMethod,
                });
            })
            .catch(err => callback(err));
    };

    invalidDetailsWarning = () => {
        const { t } = this.props;
        let id = 'invalid-card-details';
        let title = t('invalid_cc_details_title');
        let body = t('invalid_cc_details_body');

        if (!this.isCreditCardNumberValid()) {
            id = 'invalid-number';
            title = t('invalid_cc_number_title');
            body = t('invalid_cc_number_body');
        } else if (!this.isCreditCardExpiryValid()) {
            id = 'invalid-expiry';
            title = t('invalid_cc_expiry_title');
            body = t('invalid_cc_expiry_body');
        } else if (!this.isBillingDetailsValid()) {
            id = 'invalid-billing-details';
            title = t('invalid_cc_billing_details_title');
            body = t('invalid_cc_billing_details_body');
        } else if (!this.isCardholderNameValid()) {
            id = 'invalid-cardholder-name';
            title = t('invalid_cc_cardholder_title');
            body = t('invalid_cc_cardholder_body');
        }

        Mobi2Go.UI.centeredAlert({
            id,
            title,
            body,
            temp: true,
        });
    };

    render() {
        const {
            loading,
            cardholder_name,
            first_name,
            last_name,
            address_1,
            address_2,
            city,
            region,
            post_code,
        } = this.state;
        const { t, payment_processor } = this.props;
        payment_processor.__stripe_form = this;
        const show_billing_details = payment_processor.shouldShowBillingDetails();
        return (
            <div className="m2g-order-confirmation-payment-cc-stripe-form">
                <div
                    className="m2g-order-confirmation-payment-cc-stripe-form-loader"
                    style={{
                        display: loading ? 'block' : 'none',
                    }}
                >
                    <span className="m2g-dot-loader">
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </span>
                </div>
                <div
                    className="m2g-order-confirmation-payment-processor-container"
                    style={{
                        display: loading ? 'none' : 'block',
                    }}
                >
                    <div
                        data-id="order-confirmation-payment-cc-stripe-form-mount-element"
                        className="m2g-order-confirmation-payment-cc-stripe-form-mount-element"
                        ref={card_element => {
                            this.card_element = card_element;
                        }}
                    />
                    {!show_billing_details && (
                        <div
                            data-id="order-confirmation-payment-cc-stripe-form-cardholder-name"
                            className="m2g-order-confirmation-payment-cc-stripe-form-cardholder-name"
                        >
                            <span className="m2g-icon m2g-icon--user" />
                            <input
                                type="text"
                                placeholder={t('cardholder_placeholder')}
                                value={cardholder_name}
                                onChange={e =>
                                    this.setState({
                                        cardholder_name: e.target.value,
                                    })
                                }
                                autoComplete="cc-name"
                            />
                        </div>
                    )}

                    {show_billing_details && (
                        <div className="m2g-order-confirmation-payment-cc-billing">
                            <div className="m2g-order-confirmation-payment-cc-form-title m2g-title-text">
                                {t('billing_details')}
                            </div>
                            <div className="m2g-order-confirmation-payment-cc-billing-input-group">
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-first-name-input m2g-input m2g-form-element--half-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-first-name
                                    placeholder={t('first_name_placeholder')}
                                    value={first_name}
                                    onChange={e =>
                                        this.setState(
                                            { first_name: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-last-name-input m2g-input m2g-form-element--half-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-last-name
                                    placeholder={t('last_name_placeholder')}
                                    value={last_name}
                                    onChange={e =>
                                        this.setState(
                                            { last_name: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-address1-input m2g-input m2g-form-element--full-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-address-1
                                    placeholder={t('address_1_placeholder')}
                                    value={address_1}
                                    onChange={e =>
                                        this.setState(
                                            { address_1: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-address2-input m2g-input m2g-form-element--full-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-address-2
                                    placeholder={t('address_2_placeholder')}
                                    value={address_2}
                                    onChange={e =>
                                        this.setState(
                                            { address_2: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-city-input m2g-input m2g-form-element--half-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-city
                                    placeholder={t('city_placeholder')}
                                    value={city}
                                    onChange={e =>
                                        this.setState(
                                            { city: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-region-input m2g-input m2g-form-element--half-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-region
                                    placeholder={t('state_placeholder')}
                                    value={region}
                                    onChange={e =>
                                        this.setState(
                                            { region: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                                <input
                                    className="m2g-order-confirmation-payment-cc-billing-postcode-input m2g-input m2g-form-element--half-width inspectletIgnore"
                                    type="text"
                                    data-credit-card-billing-postcode
                                    placeholder={t('post_code_placeholder')}
                                    value={post_code}
                                    onChange={e =>
                                        this.setState(
                                            { post_code: e.target.value },
                                            this.onUpdateCreditCard
                                        )
                                    }
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default forwardRefWithTranslation('order.confirmation')(
    StripeCardElement
);
